import Service, { inject as service } from '@ember/service';
import { GetPublicEmployeeRequest, ListPublicEmployeesRequest } from '@square/dashboard-protos/models/squareup/payroll/service';
import PublicPayrollService from '@square/dashboard-data/clients/payroll';
import * as Sentry from '@sentry/ember';
import { feature } from '@square/ember-features/macros';

// eslint-disable-next-line ember/no-classic-classes
export default Service.extend({
  i18n: service(),
  displayMissingPayrollInfo: feature('employee/display_missing_payroll_info'),
  init(...args) {
    this._super(...args);

    // To let components rerender when loaded employees are changed, we replace this map on every change
    this.set('publicEmployees', new Map());
  },
  async loadEmployee(employeeToken) {
    if (!this.displayMissingPayrollInfo) {
      return;
    }
    try {
      const publicEmployeesResponse = await PublicPayrollService.getPublicEmployee(GetPublicEmployeeRequest.create({
        token: employeeToken
      }));
      this.setPublicEmployee(publicEmployeesResponse.publicEmployee);
    } catch (e) {
      if (e.responseStatus === 404) {
        this.setPublicEmployee(this.emptyEmployee(employeeToken));
      } else {
        throw e;
      }
    }
  },
  emptyEmployee(token) {
    return {
      token,
      hasW2EmploymentType: false,
      hasContractorEmploymentType: false,
      isEmployee: false,
      isContractor: false,
      remainingSickLeaveHours: 0,
      remainingPtoHours: 0
    };
  },
  async loadEmployees(employeeTokens, {
    batchSize = 200
  } = {}) {
    try {
      let tokensToFetch = employeeTokens.filter(employeeToken => !this.publicEmployees.has(employeeToken));
      while (tokensToFetch.length > 0) {
        await this._loadEmployeesForBatch(tokensToFetch.slice(0, batchSize));
        tokensToFetch = tokensToFetch.slice(batchSize);
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  },
  async _loadEmployeesForBatch(employeeTokens) {
    const publicEmployeesResponse = await PublicPayrollService.listPublicEmployees(ListPublicEmployeesRequest.create({
      token: employeeTokens
    }));
    const newEntries = publicEmployeesResponse.publicEmployee.map(publicEmployee => {
      return [publicEmployee.token, publicEmployee];
    });
    this.set('publicEmployees', new Map([...this.publicEmployees.entries(), ...newEntries]));
  },
  isAddedToPayroll(employeeToken) {
    const publicEmployee = this.publicEmployees.get(employeeToken);
    return Boolean(publicEmployee) && (publicEmployee.isEmployee || publicEmployee.isContractor);
  },
  employmentType(employeeToken) {
    const publicEmployee = this.publicEmployees.get(employeeToken);
    if (!publicEmployee) {
      return '';
    }
    const {
      hasW2EmploymentType,
      hasContractorEmploymentType
    } = publicEmployee;
    if (hasW2EmploymentType && hasContractorEmploymentType) {
      return this.i18n.t('employees.permissions.employeeList.edit.payrollCard.employeeContractor');
    }
    if (hasW2EmploymentType) {
      return this.i18n.t('employees.permissions.employeeList.edit.payrollCard.employee');
    }
    if (hasContractorEmploymentType) {
      return this.i18n.t('employees.permissions.employeeList.edit.payrollCard.contractor');
    }
    return '–';
  },
  missingInfo(employeeToken) {
    const {
      isMissingEmploymentInfo,
      isMissingPersonalInfo,
      isMissingTaxInfo
    } = this.publicEmployees.get(employeeToken) ?? {};
    return {
      isMissingEmploymentInfo,
      isMissingPersonalInfo,
      isMissingTaxInfo
    };
  },
  needsSelfOnboarding(employeeToken) {
    const publicEmployee = this.publicEmployees.get(employeeToken);
    return publicEmployee?.needsSelfOnboarding ?? false;
  },
  remainingPTOHours(employeeToken) {
    const publicEmployee = this.publicEmployees.get(employeeToken);
    return publicEmployee?.remainingPtoHours ?? 0;
  },
  remainingSickLeaveHours(employeeToken) {
    const publicEmployee = this.publicEmployees.get(employeeToken);
    return publicEmployee?.remainingSickLeaveHours ?? 0;
  },
  isW2Employee(employeeToken) {
    const publicEmployee = this.publicEmployees.get(employeeToken);
    return publicEmployee?.hasW2EmploymentType ?? false;
  },
  setPublicEmployee(publicEmployee) {
    this.set('publicEmployees', new Map([...this.publicEmployees.entries(), [publicEmployee.token, publicEmployee]]));
  }
});